import { type } from 'arktype';
import { dayKeySchema } from './day-key.model';
import type { BookingEntryMethod, MyWeekReviewStatus } from './my-week.model';

/** Source badge of a booking-journal row (story 35-3, F16c). */
export const bookingJournalSources = ['plan', 'stamp', 'manual', 'absence'] as const;
export type BookingJournalSource = (typeof bookingJournalSources)[number];

/**
 * One ledger row of the person booking journal: a `booking_records` entry of
 * the requested month, mapped to the HR-facing source badge. Absence markers
 * surface through their ledger bookings (`sourceType absence.*` from the
 * timetracking AND calendar modules); synthetic `expected_workday` rows are
 * excluded.
 */
export interface BookingJournalEntry {
  id: string;
  bookingDate: string;
  source: BookingJournalSource;
  /** Absence type key (the `absence.<type>` suffix) — only for `source: 'absence'`. */
  absenceType: string | null;
  startTime: string | null;
  endTime: string | null;
  breakMinutes: number;
  /** Net worked minutes (gross span minus break); 0 for untimed rows. */
  minutes: number;
  reviewStatus: MyWeekReviewStatus;
  /** `true` for a still-open clock stamp (no endTime yet). */
  open: boolean;
  /** `true` when the row lies AFTER the server-side today (future plan, spec §3d-18). */
  planned: boolean;
  homeOffice: boolean;
  note: string | null;
  entryMethod: BookingEntryMethod | null;
  /** Reviewer comment of a rejection (`metadata.rejection.comment`, spec §3d-12); null unless rejected. */
  rejectionComment: string | null;
}

/** Where the contractual daily target of a journal day was resolved from. */
export type JournalScheduleSource = 'override' | 'positionDefault' | 'legacyPeriod';

/**
 * One day row of the journal (spec §3d-19): contractual target (Soll) from
 * the `EffectiveWorkScheduleService` fallback chain, actual net minutes (Ist)
 * from the ledger entries, and the difference (Delta; null for future days).
 */
export interface BookingJournalDay {
  date: string;
  /** Contractual daily target in minutes (Soll). */
  targetMinutes: number;
  /** Net worked minutes of the day's ledger entries (Ist). */
  actualMinutes: number;
  /** `actualMinutes − targetMinutes`; null for days after the server today. */
  deltaMinutes: number | null;
  /** `true` when the day lies AFTER the server-side today (spec §3d-18). */
  planned: boolean;
  /** Fallback-chain step that resolved the target (spec §3d-19). */
  scheduleSource: JournalScheduleSource;
}

/** Response of `GET core/persons/:personId/time-tracking/bookings-journal`. */
export interface BookingJournalResponse {
  personId: string;
  /** Requested month (`YYYY-MM`). */
  month: string;
  /** Server-side today (`YYYY-MM-DD`) — the Ist/Plan cutoff (spec §3d-18). */
  todayKey: string;
  entries: BookingJournalEntry[];
  /** Per-day Soll/Ist/Delta rows (spec §3d-19), ascending by date. */
  days: BookingJournalDay[];
  totals: {
    /** Net minutes of entries up to and including the server today ("Ist bis heute"). */
    actualMinutes: number;
    /** Net minutes of entries after the server today ("Geplant"). */
    plannedMinutes: number;
  };
}

/** Confirmation trail of a day-detail booking (`metadata.confirmation`). */
export interface JournalBookingConfirmation {
  confirmedAt: string;
  deviationReason: string | null;
  /** Plan baseline captured when the confirmation corrected the times (Korrekturhistorie). */
  original: { startTime: string | null; endTime: string | null; breakMinutes: number } | null;
}

/** Reviewer decision trail of a day-detail booking (`metadata.approval` / `metadata.rejection`). */
export interface JournalBookingDecision {
  /** Display name of the deciding reviewer; null when the account is unknown. */
  byName: string | null;
  at: string;
  /** Approval justification or rejection comment. */
  comment: string | null;
}

/** One booking of the day detail (spec §3d-19) with its full audit trail. */
export interface BookingJournalDayBooking {
  id: string;
  source: BookingJournalSource;
  absenceType: string | null;
  startTime: string | null;
  endTime: string | null;
  breakMinutes: number;
  minutes: number;
  reviewStatus: MyWeekReviewStatus;
  open: boolean;
  homeOffice: boolean;
  note: string | null;
  entryMethod: BookingEntryMethod | null;
  confirmation: JournalBookingConfirmation | null;
  /** Auto-approval stamp (`metadata.autoApproval`); null when decided by a person. */
  autoApproval: { toleranceMinutes: number; decidedAt: string } | null;
  approval: JournalBookingDecision | null;
  rejection: JournalBookingDecision | null;
  /** `'clock_event'` when a re-stamp reopened the released day (spec §3a-2). */
  reopenedBy: string | null;
}

/** Origin category of a day-detail posting (derived from the idempotency key). */
export type JournalPostingOrigin = 'booking' | 'verdict' | 'reversal';

/**
 * One balance posting of the day detail (spec §3d-19). `origin: 'verdict'`
 * marks surcharge credits — `ruleKey` then carries the RuleKind key so the
 * client can name the rule ("Zuschlags-Herkunft").
 */
export interface BookingJournalDayPosting {
  id: string;
  balanceType: string;
  /** Balance type label (fallback: the type key). */
  label: string;
  amountHours: number;
  amountDays: number;
  ruleKey: string;
  origin: JournalPostingOrigin;
  /** Technical posting note (`metadata.note`) — surfaced only behind details. */
  note: string | null;
}

/** Response of `GET core/persons/:personId/time-tracking/bookings-journal/:date` (spec §3d-19). */
export interface BookingJournalDayDetail {
  personId: string;
  date: string;
  todayKey: string;
  schedule: {
    source: JournalScheduleSource;
    targetMinutes: number;
    workingDay: boolean;
    /** Work pattern name for pattern-based resolutions; null for the legacy fallback. */
    patternName: string | null;
  };
  actualMinutes: number;
  /** `actualMinutes − targetMinutes`; null for days after the server today. */
  deltaMinutes: number | null;
  bookings: BookingJournalDayBooking[];
  postings: BookingJournalDayPosting[];
}

/** `?month=YYYY-MM` query of the booking journal — shape + calendar validity. */
export const journalMonthSchema = type(/^\d{4}-\d{2}$/).narrow((month, ctx) => {
  const monthNumber = Number(month.slice(5, 7));
  return (monthNumber >= 1 && monthNumber <= 12) || ctx.mustBe('a valid calendar month (YYYY-MM)');
});

export const journalDaySchema = dayKeySchema;
